import { useState, useEffect } from "react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { KpiCard, SectionCard, StatusBadge, Empty } from "../components/shared";
import { inr, inrShort, pct, fmtDate } from "../lib/format";
import {
  BarChart, Bar as RBar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, RadialBarChart, RadialBar, Legend,
} from "recharts";
import { Wallet, TrendingUp, Clock, AlertTriangle, PackageX, Sparkles } from "lucide-react";

const COLORS = ["#0D9488", "#059669", "#0F172A", "#F59E0B", "#64748B", "#38BDF8", "#A78BFA"];

export default function Dashboard() {
  const { branch, openAI } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/dashboard", { params: { branch } }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [branch]);

  if (loading || !data) return <div className="text-brand-slate">Loading dashboard…</div>;
  const k = data.kpis;
  const sla = [{ name: "SLA", value: Math.round(k.sla_compliance * 100), fill: "#0D9488" }];

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">Executive Dashboard</h1>
          <p className="text-brand-slate text-sm mt-1">Procurement performance {branch === "All" ? "across all branches" : `for ${branch}`} · FY 2026</p>
        </div>
        <button data-testid="dashboard-ai-summary" onClick={() => openAI("")}
          className="flex items-center gap-2 text-sm font-semibold text-brand-teal bg-teal-50 hover:bg-teal-100 px-4 py-2 rounded-lg transition-colors">
          <Sparkles className="w-4 h-4" /> AI Monthly Summary
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <KpiCard testid="kpi-spend" label="Total Spend" value={inrShort(k.total_spend)} sub={`${k.order_count} orders`} icon={Wallet} tone="navy" />
        <KpiCard testid="kpi-saved" label="Amount Saved" value={inrShort(k.total_saved)} sub={`${pct(k.savings_pct)} savings`} icon={TrendingUp} tone="emerald" />
        <KpiCard testid="kpi-pending" label="Pending POs" value={k.pending_pos} sub="active orders" icon={Clock} tone="amber" />
        <KpiCard testid="kpi-delay" label="Vendor Delay Rate" value={pct(k.delay_rate)} sub="of delivered orders" icon={AlertTriangle} tone={k.delay_rate > 0.2 ? "rose" : "teal"} />
        <KpiCard testid="kpi-lowstock" label="Low Stock Alerts" value={k.low_stock_alerts} sub="items to reorder" icon={PackageX} tone={k.low_stock_alerts > 0 ? "rose" : "emerald"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="Monthly Quoted vs Final Spend" className="lg:col-span-2">
          {data.month_series.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.month_series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} />
                <YAxis tickFormatter={inrShort} tick={{ fontSize: 12, fill: "#64748B" }} width={55} />
                <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <RBar dataKey="quoted" name="Quoted" fill="#64748B" radius={[4, 4, 0, 0]} />
                <RBar dataKey="final" name="Final" fill="#0D9488" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty msg="No spend data in range" />}
        </SectionCard>

        <SectionCard title="Delivery SLA Compliance">
          <ResponsiveContainer width="100%" height={200}>
            <RadialBarChart innerRadius="70%" outerRadius="100%" data={sla} startAngle={90} endAngle={-270}>
              <RadialBar dataKey="value" cornerRadius={10} background />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="text-center -mt-32 mb-16">
            <div className="font-heading text-4xl font-bold text-navy">{Math.round(k.sla_compliance * 100)}%</div>
            <div className="text-xs text-brand-slate uppercase tracking-widest mt-1">On-time delivery</div>
          </div>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Category-wise Spend">
          {data.category_series.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.category_series} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2}>
                  {data.category_series.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty msg="No category data" />}
        </SectionCard>

        <SectionCard title="Branch-wise Spend">
          {data.branch_series.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.branch_series} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
                <XAxis type="number" tickFormatter={inrShort} tick={{ fontSize: 12, fill: "#64748B" }} />
                <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 12, fill: "#64748B" }} />
                <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                <RBar dataKey="value" name="Spend" fill="#0F172A" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty msg="No branch data" />}
        </SectionCard>
      </div>

      <SectionCard title={`Pending Payments (${data.pending_payments.length})`}>
        {data.pending_payments.length ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-border">
                {["Item", "Vendor", "Branch", "Amount", "PO Date", "Status"].map((h) => (
                  <th key={h} className="text-left uppercase text-[11px] tracking-wider text-brand-slate font-semibold px-4 py-2">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {data.pending_payments.map((p, i) => (
                  <tr key={i} className="border-b border-border/60 hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-medium text-navy">{p.item_name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{p.vendor_name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{p.branch}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-navy tabular-nums">{inr(p.amount)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{fmtDate(p.po_date)}</td>
                    <td className="px-4 py-3"><StatusBadge value={p.payment_status} type="payment" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty icon={Wallet} msg="No pending payments — all clear!" />}
      </SectionCard>
    </div>
  );
}
